// Import React and Motion
import { useState, useMemo } from 'react';
import { motion } from "framer-motion";

// Import Lucide icons
import { 
   ListChecks, X, Clock, Layers, Activity, Brain, 
   Users, Hash, Siren, Lock, Radio, Zap
} from "lucide-react";

// Import Helpers File
import { RenderRankedRecommendations } from "./DanvinciHelpers"

import { ImageDetailModal } from "./ImageModal/ImageModal";

// Import New Tab Components
import { AudienceTab } from "./VisionTabs/AudienceTab";
import { PersuasionTab } from "./VisionTabs/PersuasionTab";
import { BrandTab } from "./VisionTabs/BrandTab";

// --- NEW COMPONENT: Kinetic Strategy Banner ---
const RenderKineticStrategy = ({ strategy }) => {
    if (!strategy) return null;
    
    const modeColors = {
        'ATTACK': 'from-red-900/40 to-slate-900',
        'DEFEND': 'from-blue-900/40 to-slate-900',
        'PERSUADE': 'from-purple-900/40 to-slate-900',
        'MOBILIZE': 'from-emerald-900/40 to-slate-900',
        'GAIN_TARGET': 'from-emerald-900/40 to-slate-900',
        'OFFENSE': 'from-red-900/40 to-slate-900'
    };

    // Handle "OFFENSE (Standard)" or simple keys
    const modeKey = strategy.mode ? strategy.mode.split(' ')[0] : 'DEFEND';
    const activeColor = modeColors[modeKey] || modeColors['DEFEND'];

    return (
        <div className={`w-full bg-gradient-to-r ${activeColor} border-y border-white/5 p-4 flex flex-col md:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-4 duration-700`}>
            <div className="flex items-center gap-4">
                <div className="p-2 bg-white/5 rounded-lg border border-white/10 relative">
                    <Siren size={20} className="text-white animate-pulse" />
                    <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full shadow-[0_0_10px_rgba(239,68,68,0.8)]"></span>
                </div>
                <div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-bold">Kinetic Mode Active</div>
                    <div className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
                        {strategy.mode?.replace(/_/g, ' ')} <span className="text-slate-500 text-lg font-normal">PROTOCOL</span>
                    </div>
                </div>
            </div>
            
            <div className="flex items-center gap-6">
                 <div className="flex items-center gap-2">
                    <Lock size={14} className={strategy.volatility_gating_applied ? "text-emerald-400" : "text-slate-500"} />
                    <div className="flex flex-col">
                        <span className="text-[9px] text-slate-500 uppercase font-bold">Volatility Gating</span>
                        <span className={`text-xs font-mono font-bold ${strategy.volatility_gating_applied ? "text-emerald-400" : "text-slate-400"}`}>
                            {strategy.volatility_gating_applied ? "ENGAGED" : "DISABLED"}
                        </span>
                    </div>
                 </div>
                 <div className="h-8 w-px bg-white/10"></div>
                 <div className="flex items-center gap-2">
                    <Radio size={14} className="text-blue-400" />
                    <div className="flex flex-col">
                        <span className="text-[9px] text-slate-500 uppercase font-bold">Strategy Cycle</span>
                        <span className="text-xs font-mono font-bold text-blue-400">
                             {new Date(strategy.last_analyzed).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} UTC
                        </span>
                    </div>
                 </div>
            </div>
        </div>
    );
};

export const ResultsContainer = ({ results, selectedImage, setSelectedImage }) => {
    // State for Tab Navigation
    const [activeTab, setActiveTab] = useState('audience');
    
    // --- Hook: Calculate Persuasion Metrics ---
    // Aggregates data from the recommended images list to populate the "Persuasion Engine" tab header
    const persuasionMetrics = useMemo(() => {
        const recommendations = results?.ranked_meta_recommendations ?? [];
        
        const count = recommendations.length;
        if (count === 0) return null;

        let totalUplift = 0;
        let totalViral = 0;
        let totalConversion = 0;
        const mediumCounts = {};

        recommendations.forEach(({predictive_performance_metrics, ...img}) => {
            // Uplift
            totalUplift += (predictive_performance_metrics.scientific_uplift_modeling?.predicted_uplift_percent || 0);
            // Viral
            totalViral += (img.viral_potential_analysis?.viral_potential_score || 0);
            // Conversion
            totalConversion += (img.estimated_conversion_rate_percent || 0);
            // Medium
            const medium = img.psychological_drivers_analysis?.recommended_medium || 'Unknown';
            mediumCounts[medium] = (mediumCounts[medium] || 0) + 1;
        });

        // Determine dominant medium
        const topMedium = Object.entries(mediumCounts).sort((a,b) => b[1] - a[1])[0];

        return {
            avgUplift: totalUplift / count,
            avgViral: totalViral / count,
            avgConversion: totalConversion / count,
            topMedium: topMedium ? topMedium[0] : 'General'
        };
    }, [results]);

    // Safety check for data
    if (!results) return null;

    // --- Helper Accessors ---
    const getAnalysis = (keyCamel, keySnake) => results?.[keyCamel] || results?.[keySnake];
    
    const psychographics = getAnalysis('campaignPsychographicSummary', 'campaign_psychographic_summary');
    const demographicAudit = getAnalysis('demographicRepresentationAudit', 'demographic_representation_audit');
    const messageConsistency = getAnalysis('messageConsistencyAnalysis', 'message_consistency_analysis');
    const brandAnalysis = getAnalysis('visualBrandAnalysis', 'visual_brand_analysis');
    const recommendationsList = results?.rankedMetaRecommendations || results?.ranked_meta_recommendations || [];
    
    // Try to find Strategic Gaps
    const strategicGaps = results?.strategicGapAnalysis || 
                          results?.visual_rhetoric_analysis?.strategicGapAnalysis || 
                          recommendationsList?.[0]?.visual_rhetoric_analysis?.strategicGapAnalysis || 
                          [];

    // Helper to format scope type string
    const formatScope = (scope) => {
        if (!scope) return "Full Scan";
        return scope.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
        <div className="space-y-10 animate-in fade-in duration-500 pb-20">
            
            {/* --- 0. Kinetic Strategy Header (The "How" Context) --- */}
            {results.kineticStrategy && (
                <div className="-mx-6 -mt-6 mb-6 rounded-t-xl overflow-hidden">
                    <RenderKineticStrategy strategy={results.kineticStrategy} />
                </div>
            )}

            {/* --- 1. Hero / Meta Info --- */}
            <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-xl p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <span>PRISM Analysis <span className="text-slate-500 font-normal">| Strategy Engine</span></span>
                    </h1>
                    <div className="flex flex-wrap gap-5 mt-2 text-xs text-slate-400">
                         {/* Scope dynamically uses scope_type */}
                         <span className="flex items-center gap-1.5">
                            <Layers size={14} className="text-slate-500"/> 
                            Scope: <span className="text-slate-200 font-medium">{formatScope(results.analysisScope?.scope_type)}</span>
                         </span>
                         
                         <span className="flex items-center gap-1.5"><ListChecks size={14} className="text-slate-500"/> Analyzed: <span className="text-slate-200 font-medium">{results.images_analyzed} Images</span></span>
                         <span className="flex items-center gap-1.5"><Clock size={14} className="text-slate-500"/> Timestamp: <span className="text-slate-200 font-medium">{new Date(results.analysis_timestamp || Date.now()).toLocaleTimeString()}</span></span>
                    </div>
                    {/* Added Analysis ID specifically UNDERNEATH the row */}
                    {results.analysis_id && (
                        <div className="text-[10px] text-slate-600 font-mono mt-1.5 flex items-center gap-1">
                            <Hash size={10} />
                            {results.analysis_id.substring(0, 7)}
                        </div>
                    )}
                </div>
                {results.images_skipped > 0 && (
                    <div className="px-3 py-1 bg-slate-900/50 rounded-full border border-slate-700 text-xs text-slate-400 flex items-center gap-2">
                         <X size={12} className="text-red-400"/> {results.images_skipped} Skipped
                    </div>
                )}
            </div>

            {/* --- 2. Enhanced Image Strategy Hero Section --- */}
            {psychographics && (
                <div className="bg-gradient-to-br from-indigo-900/40 to-slate-900/80 backdrop-blur-md border border-indigo-500/30 rounded-xl p-6 relative overflow-hidden">
                    {/* Background Glow */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-3xl rounded-full -mr-10 -mt-10 pointer-events-none"></div>
                    
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-indigo-500/20 rounded-lg border border-indigo-500/30">
                                <Brain size={24} className="text-indigo-400" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Visual Strategy Intelligence</h3>
                                <p className="text-sm text-slate-400">AI-driven image selection optimized for neuro-cognitive impact</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Primary Archetype Driver */}
                            <div className="bg-slate-800/60 p-4 rounded-lg border border-slate-700/50">
                                <div className="text-xs text-indigo-300 uppercase tracking-wider mb-2">Primary Archetype</div>
                                <div className="text-lg font-bold text-white mb-1">
                                    {Object.entries(psychographics.archetype_mix || {})
                                        .sort(([,a], [,b]) => b - a)[0]?.[0].replace(/_/g, ' ') || 'Mixed'}
                                </div>
                                <div className="text-xs text-slate-400">
                                    {/* Handle potentially small float numbers or 0-100 range */}
                                    {(() => {
                                        const val = Object.entries(psychographics.archetype_mix || {})
                                        .sort(([,a], [,b]) => b - a)[0]?.[1] || 0;
                                        return val > 1 ? val.toFixed(1) : (val * 100).toFixed(1);
                                    })()}% dominance
                                </div>
                            </div>
                            {/* Cognitive Route */}
                            <div className="bg-slate-800/60 p-4 rounded-lg border border-slate-700/50">
                                <div className="text-xs text-indigo-300 uppercase tracking-wider mb-2">Decision Route</div>
                                <div className="text-lg font-bold text-white mb-1">
                                    {(psychographics.ocean_openness || 0) > 0.6 ? 'Central Route' : 'Peripheral Route'}
                                </div>
                                <div className="text-xs text-slate-400">
                                    {(psychographics.ocean_openness || 0) > 0.6 
                                        ? 'Logic & argument driven'
                                        : 'Emotion & cue driven'}
                                </div>
                            </div>
                            {/* Visual Complexity Preference */}
                            <div className="bg-slate-800/60 p-4 rounded-lg border border-slate-700/50">
                                <div className="text-xs text-indigo-300 uppercase tracking-wider mb-2">Complexity Tolerance</div>
                                <div className="text-lg font-bold text-white mb-1">
                                    {(psychographics.ocean_conscientiousness || 0) > 0.6 ? 'High' : 'Low'}
                                </div>
                                <div className="text-xs text-slate-400">
                                    {(psychographics.ocean_conscientiousness || 0) > 0.6
                                        ? 'Detailed visuals effective'
                                        : 'Simple imagery preferred'}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- 3. The Proof (Top Photos) - KEEPING AT TOP LEVEL --- */}
            <section>
                <RenderRankedRecommendations 
                    list={recommendationsList} 
                    onImageClick={setSelectedImage} 
                />
            </section>

            {/* -------------------------------------------------------- */}
            {/* START TABBED INTERFACE                                   */}
            {/* -------------------------------------------------------- */}
            
            {/* HIGH-TECH TAB NAVIGATION - DANTE WHITE GLOW (REFINED) */}
            <div className="flex items-center space-x-1 md:space-x-8 mb-8 border-b border-slate-700/50 relative">
                {[
                    { id: 'audience', label: 'Audience & Profile', icon: Users },
                    { id: 'persuasion', label: 'Persuasion Engine', icon: Zap },
                    { id: 'brand', label: 'Brand & Health', icon: Activity }
                ].map((tab) => {
                    const isActive = activeTab === tab.id;
                    const Icon = tab.icon;
                    
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`
                                relative py-4 px-2 flex items-center space-x-2 text-xs md:text-sm font-bold uppercase tracking-widest transition-all duration-300 outline-none group
                                ${isActive ? 'text-white' : 'text-slate-500 hover:text-slate-300'}
                            `}
                        >
                            {/* Icon with DANTE WHITE GLOW */}
                            <Icon 
                                size={16} 
                                className={`transition-all duration-300 ${isActive ? "text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]" : "group-hover:text-slate-300"}`} 
                            />
                            
                            {/* Text Label with DANTE WHITE GLOW */}
                            <span className={`hidden md:block ${isActive ? "drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" : ""}`}>
                                {tab.label}
                            </span>
                            
                            {/* Mobile Text Label */}
                            <span className={`md:hidden ${isActive ? "drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]" : ""}`}>
                                {tab.label.split(' ')[0]}
                            </span>

                            {/* Active Tab Indicator (Refined White Glow) */}
                            {isActive && (
                                <motion.div 
                                    layoutId="activeTab"
                                    // Reduced spread to 1px and opacity to 0.5 for a tighter, cleaner glow
                                    className="absolute bottom-0 left-0 right-0 h-[2px] bg-white shadow-[0_0_10px_1px_rgba(255,255,255,0.5)]"
                                    initial={false}
                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                />
                            )}
                            
                            {/* Subtle White Ambient Glow Background */}
                            {isActive && (
                            <div className="absolute inset-0 bg-gradient-to-t from-white/5 to-transparent opacity-20 blur-md -z-10" /> 
                            )}
                        </button>
                    );
                })}
            </div>

            {/* TAB CONTENT AREA */}
            <main className="min-h-[400px]">
                {activeTab === 'audience' && (
                    <AudienceTab 
                        psychographics={psychographics}
                        results={results}
                        demographicAudit={demographicAudit}
                    />
                )}
                
                {activeTab === 'persuasion' && (
                    <PersuasionTab 
                        persuasionMetrics={persuasionMetrics}
                        messageConsistency={messageConsistency}
                        recommendationsList={recommendationsList}
                        strategicGaps={strategicGaps}
                    />
                )}
                
                {activeTab === 'brand' && (
                    <BrandTab 
                        brandAnalysis={brandAnalysis}
                        messageConsistency={messageConsistency}
                        demographicAudit={demographicAudit}
                        psychographics={psychographics}
                    />
                )}
            </main>

            {/* --- Modal Handling --- */}
            {selectedImage && (
                <ImageDetailModal 
                    image={selectedImage}
                    onClose={() => setSelectedImage(null)}
                    onNext={() => {}} 
                    onPrev={() => {}}
                    isFirst={false}
                    isLast={false}
                    // NEW: Passing context for resonance calculation
                    audienceContext={psychographics}
                />
            )}
        </div>
    );
};